/// <amd-module name="RMWidgets/DeploymentStatusWidget/DSWContracts" />
import { IStatusProps } from "VSSUI/Status";

import { BuildRepository } from "RMWidgets/DeploymentStatusWidget/Clients/Contracts/Build";
import { WidgetSize } from "Dashboards/WidgetContracts/WidgetProps";

import { DefinitionEnvironmentReference } from "Release/Client/Contracts/Release";

export enum DSWLastColumnType {
    None = 0,
    LastDeployed = 1,
    FailingSince = 2
}

/**
* Describes the contract of the data being shared by the widget and the configuration
*/
export interface IDSWCustomSettings {
    buildDefinitionId: number;
    projectId: string;
    useDefaultBranch: boolean;
    branchName?: string;
    buildSourceType: string;
    environments: DefinitionEnvironmentReference[];
    lastColumnType: DSWLastColumnType;
    viewType: string;
    viewTypeIdentifier: number;
    ruleOperators: string[];
    ruleBoundaries: string[];
    ruleBoundariesErrors: boolean[];
    repository: BuildRepository;
}

export interface IDSWBuildInfo {
    id: number;
    name: string;
    url: string;
    tooltip: DSWTooltip;
}

export interface IDSWBuildDefinitionInfo {
    name: string;
    url: string;
}

export interface IDSWLastColumnInfo extends IDSWDeploymentInfo {
    buildId: number;
    buildName: string;
    buildUrl: string;
    buildStatus: string;
}

export interface IDSWTestResultInfo {
    releaseId: number;
    releaseEnvironmentId: number;
    passPercentage: string;
    testResultsUrl: string;
    testStatusColor: string;
    testStatusProps: IStatusProps;
    toolTip: DSWTooltip;
}

export interface IDSWDeploymentInfo {
    deploymentId: number;
    releaseId: number;
    releaseName: string;
    releaseEnvironmentId: number;
    releaseEnvironmentName: string;
    releaseSummaryUrl: string;
    statusColourCss: string;
    statusProps: IStatusProps;
    toolTip: DSWTooltip; 
}

export interface IDSWEnvironmentInfo {
    definitionEnvironmentId: number;
    definitionEnvironmentName: string;
    releaseDefinitionId: number;
    releaseDefinitionName: string;
    environmentSummaryUrl: string;
    tooltip: string[];
}

export interface IReleaseEnvironmentReference {
    releaseId: number;
    environmentId: number;
}

export class WidgetSizeMapping {
    public static getNumberOfBuilds(settings: IDSWCustomSettings, size: WidgetSize): number {
        if (
            !size
            || size.width < this.minWidth
            || size.width > this.maxWidth
        ) {
            return 0;
        }

        let noOfColumns = this.widthToColumnsMapping[size.width];
        let viewType: DSWLastColumnType = settings.lastColumnType;

        return viewType === DSWLastColumnType.FailingSince || viewType === DSWLastColumnType.LastDeployed
            ? noOfColumns - 2 : noOfColumns;
    }

    public static getNumberOfDefinitions(size: WidgetSize): number {
        if (!size
            || size.height < this.minHeight
            || size.height > this.maxHeight) {

            return 0;
        }

        return this.heightToRowsMapping[size.height];
    }

    private static widthToColumnsMapping: { [width: number]: number; } = { 500: 5, 670: 7, 840: 10, 1010: 13, 1180: 16, 1350: 19, 1520: 22, 1690: 24 };
    private static heightToRowsMapping: { [height: number]: number; } = { 160: 1, 330: 6, 500: 11, 670: 16 };

    private static minHeight: 160;
    private static maxHeight = 670;
    private static minWidth = 500;
    private static maxWidth = 1690;
}

export class TestRateRuleOperators {
    public static LessThan: string = "<";
    public static LessThanEquals: string = "<=";
    public static GreaterThan: string = ">";
    public static GreaterThanEquals: string = ">=";
    public static Equals: string = "=";
}

export class TestRateRuleColors {
    public static Red: string = "red";
    public static Orange: string = "orange";
    public static Green: string = "green";
}

export class DSWTooltip {
    build?: string;
    buildStatus?: string;
    release?: string;
    deploymentStatus?: string;
    environment?: string;
    testsPassed?: string;
    deploymentDuration?: string;
    testRunDuration?: string;
    abortedRuns?: string;
}

export class DSWViewType {
    public static DeploymentStatusIdentifier: number = 1; 
    public static TestPassRateIdentifier: number = 2;
    public static DeploymentStatus: string = "Deployment Status";
    public static TestPassRate: string = "Test Pass Rate";

    public static checkViewType(viewType : string,
        expectedViewType : string,
        viewTypeIdentifier : number,
        expectedViewtypeIdentifier : number) : boolean {
        if (viewTypeIdentifier) {
            return viewTypeIdentifier === expectedViewtypeIdentifier;
        }

        return viewType === expectedViewType;
    }
}
